/*@flow*/
import { type Dataview } from 'lib/dataviews/types';
import checkResponseStatus from 'lib/checkResponseStatus';
import qs from 'qs';
import Task from 'folktale/concurrency/task';

// ANALYSIS
//
// =====================================
type Analysis = {
  id: string,
  type: 'source',
  params: {
    query: string,
  },
};

// LAYER
//
// =====================================
type Layer = {
  type: 'cartodb',
  options: $Shape<{
    cartocss: string,
    cartocss_version: '2.3.0',
    interactivity: string,
    sql: string,
    source: {
      id: string,
    },
    layerIndex: number,
  }>,
  visible?: boolean,
};

type Config = {
  analyses?: Analysis[],
  layers: Layer[],
  dataviews?: { [key: string]: Dataview },
};

// WIRE
//
// =====================================

type Subdomains = [string, string, string, string];

type CDNUrl = {
  http: string,
  https: string,
  templates: {
    http: { subdomains: Subdomains, url: string },
    https: { subdomains: Subdomains, url: string },
  },
};

type RasterTileUrls = [string, string, string, string];
export type VectorTileUrls = [string, string, string, string];

type TileJSONVersion = string;

type TileJSONConfig<T> = {
  tilejson: TileJSONVersion,
  tiles: T,
};

type UrlConfig = {
  urlTemplate: string,
  subdomains: Subdomains,
};

type CreatedAnalysis = {
  nodes: {
    [id: string]: {
      status: 'ready',
      query: string,
    },
  },
};

type CreatedLayer = {
  id: string,
  type: 'mapnik',
  meta: {
    cartocss: string,
    cartocss_meta: { rules: any[] },
    stats: { estimatedFeatureCount: number },
  },
  tilejson: {
    raster: TileJSONConfig<RasterTileUrls>,
    vector: TileJSONConfig<VectorTileUrls>,
  },
};

type CreatedDataviews = {
  [id: string]: {
    url: {
      http: string,
      https: string,
    },
  },
};

type Metadata = {
  analyses: CreatedAnalysis[],
  dataviews: CreatedDataviews,
  layers: CreatedLayer[],

  tilejson: {
    raster: TileJSONConfig<RasterTileUrls>,
    vector: TileJSONConfig<VectorTileUrls>,
  },
  url: {
    raster: UrlConfig,
    vector: UrlConfig,
  },
};

const mkUrl = ({
  userName,
  apiKey,
  ...rest
}: $Shape<{
  userName: string,
  apiKey?: string,
  config: string,
  filters: string,
}>) => {
  let query = apiKey ? `?map_key=${apiKey}` : '';
  if (rest) {
    const params = qs.stringify(rest);
    if (query[0] === '?') {
      query = `${query}&${params}`;
    } else {
      query = `?${query}${params}`;
    }
  }
  return `https://${userName}.cartodb.com:443/api/v1/map${query}`;
};

export type CartoResponse = {
  layergroupid: string,
  cdn_url: CDNUrl,
  last_updated: string,
  metadata: Metadata,
};

type RequestOpts = {
  params?: $Shape<{
    apiKey?: string,
    userName?: string,
    filters?: string,
  }>,
};

export const getAnalysis = (mapConfig: CartoResponse, id: string) => {
  const analyses = mapConfig.metadata.analyses;
  const analysis = analyses.find(v => Object.keys(v.nodes).includes(id));
  if (!analysis) return;
  const { nodes } = analysis;
  return nodes[id];
};

const getCartoMap = (config: Config, requestOpts?: RequestOpts = {}) => {
  const { userName, apiKey, filters, ...rest } = requestOpts.params || {};

  const url = mkUrl({
    ...rest,
    userName: userName || 'swc28',
    apiKey,
    config: JSON.stringify(config),
    filters: JSON.stringify(filters || {}),
  });

  return fetch(url, {
    method: 'get',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    mode: 'cors',
  })
    .then(checkResponseStatus)
    .then(res => res.json())
    .then((res: CartoResponse) => res);
};

export default getCartoMap;

export const getCartoMapTask = Task.fromPromised(getCartoMap);
